import { animated, useSpring } from '@react-spring/web'
import React, { MouseEventHandler, useLayoutEffect, useRef, useState } from 'react'
import { Caret } from '~/components/Icons'
import type { HTMLElementProps } from '~/types'
import { cn, range } from '~/utils'

type Props = HTMLElementProps<
	'div',
	{
		title: React.ReactNode
		summaryClassname?: Props['className']
	}
>

export const Collapsible: React.FC<Props> = ({ title, className, summaryClassname, children, ...props }) => {
	const [open, setOpen] = useState(true)
	const [expanding, setExpanding] = useState(false)
	const [contentHeight, setHeight] = useState(0)
	const contentRef = useRef<HTMLDivElement>(null)
	const AnimatedCaret = animated(Caret)

	// Setting dialog[open] to false prevents content from rendering,
	// so we set open to true and immediately close it to calculate content height
	useLayoutEffect(() => {
		setOpen(false)
		// Recheck height in case content has unloaded images
		const timeouts = range(0, 20).map(i =>
			setTimeout(() => {
				setHeight(contentRef.current?.scrollHeight || 1)
			}, i * 200)
		)
		return () => {
			timeouts.map(clearTimeout)
		}
	}, [])
	useLayoutEffect(() => {
		setOpen(false)
	}, [children])

	const [{ height, rotate }, api] = useSpring(() => ({
		height: 0,
		rotate: 0,
		onRest: {
			height: h => {
				setExpanding(false)
				if (h.value === 0) setOpen(false)
			}
		}
	}))

	const onClick: MouseEventHandler<HTMLElement> = e => {
		e.preventDefault()
		if (!open) setOpen(true)
		api.start(height.goal == 0 ? { height: contentHeight, rotate: -180 } : { height: 0, rotate: 0 })
		setExpanding(true)
	}

	return (
		<div
			aria-expanded={open}
			role="dialog"
			aria-modal
			onClick={e => e.preventDefault()}
			className={cn(className, 'overflow-hidden')}
			aria-busy={expanding}
			{...props}
		>
			<div
				role="button"
				onClick={onClick}
				className={cn(
					'hocus:opacity-60 cursor-pointer select-none leading-none focus-visible:opacity-60',
					summaryClassname
				)}
			>
				{title}
				<AnimatedCaret className="ml-2 align-middle" style={{ rotate }} />
			</div>
			<animated.div style={{ height }}>
				<div ref={contentRef} className="overflow-visible">
					{children}
				</div>
			</animated.div>
		</div>
	)
}
